import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import {
  EOrderStatus,
  IQueryPosition,
  IQueryPositionDetail,
} from "../../interfaces/IQuery";
import {
  IPositions,
  IResponsePositionGraphic,
  IResponsePositions,
  IResponsePositionsDetail,
} from "../../interfaces/OrderPostions";
import { ERole } from "../../interfaces/User/newUser";
import { endDateToApiString, startDateToApiString } from "../../utils/date";

export const getPosition = async (
  {
    order_status,
    order_side,
    is_open,
    page,
    pageSize,
    productId,
    orderBy,
    sortOrder,
    open_date_end,
    open_date_start,
    roleSelected,
    client_id,
  }: IQueryPosition,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!open_date_start)
    params.append(
      "open_date_start",
      startDateToApiString(open_date_start) || ""
    );
  if (!!open_date_end)
    params.append("open_date_end", endDateToApiString(open_date_end) || "");
  if (!!order_status && order_status.length > 0) {
    order_status.forEach((status) =>
      status !== EOrderStatus.ALL ? params.append("order_status", status) : null
    );
  }
  if (!!productId && productId.length > 0) {
    productId.forEach((product) =>
      !!product ? params.append("product_id", product.toString()) : null
    );
  }
  if (!!order_side) params.append("order_side", order_side);
  if (!!is_open) params.append("is_open", is_open.toString());
  if (!!page) params.append("page", page.toString());
  if (!!pageSize) params.append("pageSize", pageSize.toString());
  if (!!orderBy) params.append("orderBy", orderBy);
  if (!!sortOrder) params.append("sortOrder", sortOrder);
  if (!!client_id) params.append("client_id", client_id);

  const isCustomer = roleSelected === ERole.client;
  const response = await api.get<IResponsePositions>(
    isCustomer ? `/web/positions/customer` : `/web/positions/analyst`,
    {
      signal,
      params,
    }
  );
  const serverTimestamp =
    response.headers["last-modified"] || new Date().toISOString();
  const returnData: IResponsePositions = {
    ...response.data,
    lastUpdated: serverTimestamp,
  };

  return returnData;
};

export const usePosition = (params: IQueryPosition) => {
  const { roleSelected } = params;

  return useQuery({
    queryKey: ["position", params],
    queryFn: async ({ signal }) => {
      const data = await getPosition(params, signal);
      return data;
    },

    refetchInterval: 1000 * 60 * 5,
    refetchIntervalInBackground: true,
    keepPreviousData: true,
    enabled: !!roleSelected,
  });
};

export const getCustomerPositionDetail = async (
  { id }: IQueryPositionDetail,
  signal?: AbortSignal
) => {
  const { data } = await api.get<IPositions[]>(
    `/web/positions/customer/detail/${id}`,
    {
      signal,
    }
  );
  return data;
};

export const useCustomerPositionDetail = (params: IQueryPositionDetail) => {
  const { id, isAnalytic } = params;

  return useQuery({
    queryKey: ["positionCustomerDetail", params],
    queryFn: ({ signal }) => getCustomerPositionDetail(params, signal),
    keepPreviousData: true,
    enabled: !!id && !isAnalytic,
  });
};
export const getAnalystPositionDetail = async (
  { id, page, pageSize, orderBy, sortOrder, client_id }: IQueryPositionDetail,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!page) params.append("page", page.toString());
  if (!!pageSize) params.append("pageSize", pageSize.toString());
  if (!!orderBy) params.append("orderBy", orderBy);
  if (!!sortOrder) params.append("sortOrder", sortOrder);
  if (!!client_id) params.append("client_id", client_id);

  const { data } = await api.get<IResponsePositionsDetail>(
    `/web/positions/analyst/detail/${id}`,
    {
      signal,
      params,
    }
  );
  return data;
};

export const useAnalystPositionDetail = (params: IQueryPositionDetail) => {
  const { id, isAnalytic } = params;

  return useQuery({
    queryKey: ["positionAnalystDetail", params],
    queryFn: ({ signal }) => getAnalystPositionDetail(params, signal),
    enabled: !!id && !!isAnalytic,
    keepPreviousData: true,
  });
};

export async function getPositionDownload({
  productId,
  open_date_end,
  open_date_start,
  is_open,
  roleSelected,
  client_id,
}: IQueryPosition) {
  const enabled = !!productId && !!open_date_end;
  let params = new URLSearchParams();
  if (!!open_date_start)
    params.append(
      "open_date_start",
      startDateToApiString(open_date_start) || ""
    );
  if (!!open_date_end)
    params.append("open_date_end", endDateToApiString(open_date_end) || "");
  if (!!is_open) params.append("is_open", is_open.toString());
  if (!!productId && productId.length > 0) {
    productId.forEach((product) =>
      !!product ? params.append("product_id", product.toString()) : null
    );
  }
  if (!!client_id) params.append("client_id", client_id);

  const isCustomer = roleSelected === ERole.client;

  try {
    if (!enabled) return;
    const response = await api.get(
      isCustomer
        ? `/web/positions/customer/download`
        : `/web/positions/analyst/download`,
      {
        responseType: "blob",
        params,
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `posições-${productId}.xlsx`);

    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  } catch (error) {
    console.error("Erro ao exportar arquivo:", error);
    throw error;
  }
}

export const getPositionGraphic = async (
  { is_open, productId, open_date_end, open_date_start }: IQueryPosition,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!open_date_start)
    params.append(
      "open_date_start",
      startDateToApiString(open_date_start) || ""
    );
  if (!!open_date_end)
    params.append("open_date_end", endDateToApiString(open_date_end) || "");
  if (!!productId && productId.length > 0) {
    productId.forEach((product) =>
      !!product ? params.append("product_id", product.toString()) : null
    );
  }
  if (!!is_open) params.append("is_open", is_open.toString());

  const { data } = await api.get<IResponsePositionGraphic>(
    `/web/positions/graph`,
    {
      signal,
      params,
    }
  );

  return data;
};

export const usePositionGraphic = (params: IQueryPosition) => {
  const { is_open, productId, open_date_end, open_date_start, roleSelected } =
    params;

  return useQuery(
    ["graphic", is_open, productId, open_date_end, open_date_start],
    async ({ signal }) => {
      const data = await getPositionGraphic(params, signal);
      return data;
    },
    {
      refetchInterval: 1000 * 60 * 5,
      refetchIntervalInBackground: true,
      keepPreviousData: true,
      enabled: !!roleSelected,
    }
  );
};
